import { CSSProperties, useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import LockIcon from '@mui/icons-material/Lock';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import EmailIcon from '@mui/icons-material/Email';
import { useAppSelector } from 'hooks';
import { Capacitor } from '@capacitor/core';
import { getAccessToken } from 'utils/auth';

function getBackofficeBase(): string {
  return process.env.REACT_APP_BACKOFFICE_URL || `https://backoffice.${process.env.REACT_APP_DOMAIN || 'hijack.poker'}`;
}

type MFAViewProps = {
  onVerified: () => void;
  phone?: string;
  maskedEmail?: string;
};

const maskPhone = (phone: string): string => {
  if (!phone || phone.length < 4) return '***-***-****';
  return `***-***-${phone.slice(-4)}`;
};

const maskEmail = (email: string): string => {
  if (!email) return '****@****.com';
  const [user, domain] = email.split('@');
  if (!user || !domain) return '****@****.com';
  return `${user[0]}***@${domain}`;
};

const MFAView = ({ onVerified, phone, maskedEmail: maskedEmailProp }: MFAViewProps) => {
  const playerEmail = useAppSelector((state) => state.user.playerEmail);
  const displayPhone = maskPhone(phone || '');
  const displayEmail = maskedEmailProp || maskEmail(playerEmail);
  const [method, setMethod] = useState<'sms' | 'email' | null>(null);
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [resendTimer, setResendTimer] = useState(0);
  const [error, setError] = useState('');
  const [_sending, setSending] = useState(false);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const otpAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (containerRef.current) {
      gsap.fromTo(containerRef.current,
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' }
      );
    }
  }, [method]);

  // Countdown timer for resend
  useEffect(() => {
    if (resendTimer <= 0) return;
    const interval = setInterval(() => setResendTimer((t) => t - 1), 1000);
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleSendCode = async (m: 'sms' | 'email') => {
    setMethod(m);
    setResendTimer(120);
    setCode(['', '', '', '', '', '']);
    setError('');
    setSending(true);

    // Call Descope OTP via backoffice proxy
    try {
      const base = getBackofficeBase();
      const formData = new URLSearchParams();
      formData.append('loginId', playerEmail);
      formData.append('type', m);

      // Use the stored Descope access token (cookie / localStorage), the same
      // source every other backoffice call in the game client uses. The
      // Descope SDK's getSessionToken() only returns a value inside the
      // <AuthProvider> (mounted on LoginScreen only), so it was null here in
      // the in-game cashier — producing `Bearer null` and a 401 on every send.
      const accessToken = getAccessToken();
      const res = await fetch(`${base}/api/descope/sendOTP`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData.toString()
      });

      if (!res.ok) {
        setError('Failed to send code. Please try again.');
      }
    } catch (e) {
      console.warn('[MFA] Failed to send OTP:', e);
      setError('Failed to send code. Please try again.');
    } finally {
      setSending(false);
    }

    setTimeout(() => inputRefs.current[0]?.focus(), 300);

    // Native SMS Retriever — Capacitor Android
    if (m === 'sms' && Capacitor.isNativePlatform()) {
      try {
        const plugin = (window as any).Capacitor?.Plugins?.SmsRetriever;
        if (plugin) {
          await plugin.startSmsReceiver();
          plugin.addListener('smsReceived', (result: any) => {
            const smsBody = result?.message || result?.sms || '';
            // Extract 4-6 digit code from SMS
            const match = smsBody.match(/\b(\d{4,6})\b/);
            if (match) {
              const digits = match[1].split('').slice(0, 6);
              const newCode = ['', '', '', '', '', ''];
              digits.forEach((d: string, idx: number) => { if (idx < 6) newCode[idx] = d; });
              setCode(newCode);
              if (newCode.every((d) => d !== '')) {
                verifyCode(newCode.join(''));
              }
            }
          });
          console.log('[MFA] Native SMS retriever started');
        }
      } catch (e) {
        console.log('[MFA] Native SMS retriever not available:', e);
      }
    }

    // WebOTP API fallback — Chrome Android browser
    if (m === 'sms' && !Capacitor.isNativePlatform() && 'OTPCredential' in window) {
      const ac = new AbortController();
      otpAbortRef.current = ac;
      (navigator.credentials as any).get({
        otp: { transport: ['sms'] },
        signal: ac.signal
      }).then((otp: any) => {
        if (otp?.code) {
          const digits = otp.code.split('').slice(0, 6);
          setCode(digits);
          // Auto-submit
          if (digits.length === 6 && digits.every((d: string) => d !== '')) {
            verifyCode(digits.join(''));
          }
        }
      }).catch(() => {
        // User dismissed or timeout — no action needed
      });
    }
  };

  const handleDigitChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value.slice(-1);
    setCode(newCode);
    setError('');

    // Auto-advance
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Auto-submit when complete
    if (newCode.every((d) => d !== '') && value) {
      const fullCode = newCode.join('');
      verifyCode(fullCode);
    }
  };

  const verifyCode = async (fullCode: string) => {
    try {
      const base = getBackofficeBase();
      const formData = new URLSearchParams();
      formData.append('code', fullCode.replace(/\D/g, ''));
      formData.append('loginId', playerEmail);
      formData.append('type', method || 'sms');

      const accessToken = getAccessToken();
      const res = await fetch(`${base}/api/descope/verifyOTP`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData.toString()
      });

      if (!res.ok) {
        setError('Invalid code. Please try again.');
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
        return;
      }

      const json = await res.json();
      if (json?.original?.status === 'ACTIVE' && json?.original?.active) {
        if (otpAbortRef.current) otpAbortRef.current.abort();
        onVerified();
      } else {
        setError('Invalid code. Please try again.');
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (e) {
      console.warn('[MFA] Verify failed:', e);
      setError('Verification failed. Please try again.');
      setCode(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  // Step 1: Choose method
  if (!method) {
    return (
      <div ref={containerRef} style={containerStyle}>
        <LockIcon style={{ fontSize: '2rem', color: '#E98C26', alignSelf: 'center' }} />
        <span style={headingStyle}>Verify Your Identity</span>
        <span style={subtextStyle}>We'll send a 6-digit code to confirm this transaction.</span>

        <button onClick={() => handleSendCode('sms')} style={methodBtnStyle}>
          <div style={methodBtnIconWrap}><PhoneAndroidIcon style={{ fontSize: '1.2rem', color: '#63b3ed' }} /></div>
          <div style={methodBtnInfo}>
            <span style={methodBtnLabel}>Text Message</span>
            <span style={methodBtnDesc}>Send code to {displayPhone}</span>
          </div>
        </button>

        <button onClick={() => handleSendCode('email')} style={methodBtnStyle}>
          <div style={methodBtnIconWrap}><EmailIcon style={{ fontSize: '1.2rem', color: '#E98C26' }} /></div>
          <div style={methodBtnInfo}>
            <span style={methodBtnLabel}>Email</span>
            <span style={methodBtnDesc}>Send code to {displayEmail}</span>
          </div>
        </button>
      </div>
    );
  }

  // Step 2: Enter code
  return (
    <div ref={containerRef} style={containerStyle}>
      <LockIcon style={{ fontSize: '2rem', color: '#E98C26', alignSelf: 'center' }} />
      <span style={headingStyle}>Enter Verification Code</span>
      <span style={subtextStyle}>
        Sent via {method === 'sms' ? 'text message' : 'email'}
      </span>

      <div style={codeRowStyle}>
        {code.map((digit, i) => (
          <input
            key={i}
            ref={(el) => { inputRefs.current[i] = el; }}
            type="text"
            inputMode="numeric"
            // First box accepts the full code and distributes — also the
            // focused input, so iOS QuickType offers the SMS suggestion here.
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            maxLength={i === 0 ? 6 : 1}
            value={digit}
            onChange={(e) => {
              // If multiple digits entered (keyboard autofill), distribute them
              const val = e.target.value.replace(/\D/g, '');
              if (val.length > 1) {
                const digits = val.slice(0, 6).split('');
                const newCode = ['', '', '', '', '', ''];
                digits.forEach((d, idx) => { if (idx < 6) newCode[idx] = d; });
                setCode(newCode);
                if (newCode.every((d) => d !== '')) {
                  verifyCode(newCode.join(''));
                }
                return;
              }
              handleDigitChange(i, e.target.value);
            }}
            onKeyDown={(e) => handleKeyDown(i, e)}
            onPaste={(e) => {
              // Handle paste of full code (e.g., from clipboard/autofill)
              const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
              if (pasted.length >= 4) {
                e.preventDefault();
                const digits = pasted.split('');
                const newCode = [...code];
                digits.forEach((d, idx) => { if (idx < 6) newCode[idx] = d; });
                setCode(newCode);
                if (newCode.every((d) => d !== '')) {
                  verifyCode(newCode.join(''));
                }
              }
            }}
            style={{
              ...codeDigitStyle,
              borderColor: error ? '#c0392b' : digit ? '#E98C26' : 'rgba(255,255,255,0.15)'
            }}
          />
        ))}
      </div>

      {error && <span style={errorStyle}>{error}</span>}

      <button
        onClick={() => handleSendCode(method)}
        disabled={resendTimer > 0}
        style={{
          ...resendBtnStyle,
          opacity: resendTimer > 0 ? 0.4 : 1
        }}
      >
        {resendTimer > 0 ? `Resend code in ${resendTimer}s` : 'Resend code'}
      </button>

      <button onClick={() => setMethod(null)} style={backLinkStyle}>
        Use a different method
      </button>
    </div>
  );
};

export default MFAView;

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
  padding: '20px 0'
};

const headingStyle: CSSProperties = {
  fontSize: '1.1rem',
  fontWeight: 700,
  color: '#ffffff',
  textAlign: 'center'
};

const subtextStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: 'rgba(255, 255, 255, 0.5)',
  textAlign: 'center'
};

const methodBtnStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  width: '100%',
  padding: '14px',
  backgroundColor: 'rgba(255, 255, 255, 0.04)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: '12px',
  cursor: 'pointer',
  fontFamily: 'inherit',
  transition: 'all 0.15s ease'
};

const methodBtnIconWrap: CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: '12px',
  backgroundColor: 'rgba(255, 255, 255, 0.06)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
};

const methodBtnInfo: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  textAlign: 'left'
};

const methodBtnLabel: CSSProperties = {
  fontSize: '0.85rem',
  fontWeight: 600,
  color: '#ffffff'
};

const methodBtnDesc: CSSProperties = {
  fontSize: '0.65rem',
  color: 'rgba(255, 255, 255, 0.4)'
};

const codeRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  gap: '8px',
  margin: '8px 0'
};

const codeDigitStyle: CSSProperties = {
  width: 44,
  height: 52,
  backgroundColor: 'rgba(255, 255, 255, 0.04)',
  border: '2px solid rgba(255, 255, 255, 0.15)',
  borderRadius: '10px',
  color: '#ffffff',
  fontSize: '1.3rem',
  fontWeight: 700,
  textAlign: 'center',
  outline: 'none',
  fontFamily: 'inherit',
  transition: 'border-color 0.15s ease'
};

const errorStyle: CSSProperties = {
  fontSize: '0.75rem',
  color: '#c0392b',
  fontWeight: 600,
  textAlign: 'center'
};

const resendBtnStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  color: '#E98C26',
  fontSize: '0.8rem',
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'inherit',
  textAlign: 'center'
};

const backLinkStyle: CSSProperties = {
  background: 'none',
  border: 'none',
  color: 'rgba(255, 255, 255, 0.4)',
  fontSize: '0.75rem',
  cursor: 'pointer',
  fontFamily: 'inherit',
  textAlign: 'center'
};
